import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Money, EmergencyBanner } from "@/components/shared/Common";
import { toast } from "sonner";
import { Calendar, Clock, ArrowRight } from "lucide-react";

const DAYS = ["sun","mon","tue","wed","thu","fri","sat"];

function nextDates(n=7) {
  const out = [];
  const now = new Date();
  for (let i=0;i<n;i++) {
    const d = new Date(now); d.setDate(now.getDate()+i);
    out.push(d);
  }
  return out;
}

export default function BookAppointment() {
  const { doctorId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [type, setType] = useState("general");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reason, setReason] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [duration, setDuration] = useState("");
  const [severity, setSeverity] = useState("");
  const [concerns, setConcerns] = useState("");
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [applyCredit, setApplyCredit] = useState(true);

  useEffect(() => {
    api.get(`/doctors/${doctorId}`).then(r => {
      setData(r.data);
      if (!(r.data.doctor.consultation_types||[]).includes("general")) setType("specialist");
    });
    api.get("/pricing").then(r => setPricing(r.data));
    api.get("/patient/credits").then(r => setCredits(r.data)).catch(()=>{});
  }, [doctorId]);

  const dates = nextDates(7);
  const slots = (data?.availability?.weekly_slots?.[DAYS[selectedDate?.getDay?.() ?? -1]] || []);
  const price = type === "specialist" ? pricing?.specialist_price : pricing?.general_price;
  const balance = credits?.balance || 0;
  const creditToApply = applyCredit ? Math.min(balance, Number(price || 0)) : 0;
  const dueNow = Math.max(0, Number(price || 0) - creditToApply);

  const submit = async () => {
    if (!selectedDate || !selectedSlot) return toast.error("Pick a date and time.");
    setLoading(true);
    try {
      const iso = new Date(`${selectedDate.toISOString().slice(0,10)}T${selectedSlot}:00`).toISOString();
      const { data: appt } = await api.post("/appointments", {
        doctor_id: doctorId,
        slot_datetime: iso,
        consultation_type: type,
        reason, symptoms, duration, severity, patient_concerns: concerns,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        appointment_id: appt.id, origin_url: window.location.origin,
        apply_credit: applyCredit,
      });
      if (checkout.paid_with_credit) {
        toast.success("Booked! Paid with your credit balance.");
        nav("/patient/appointments");
      } else {
        window.location.href = checkout.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
      setLoading(false);
    }
  };

  if (!data) return <div className="text-center text-[#475569]">Loading…</div>;
  const d = data.doctor;

  return (
    <div className="space-y-6">
      <div>
        <div className="cm-eyebrow mb-2">Book appointment</div>
        <h1 className="cm-h2">Book with Dr. {d.first_name} {d.last_name}</h1>
      </div>
      <EmergencyBanner compact/>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="cm-card">
            <h3 className="font-semibold text-[#1a2b4a] mb-3">Consultation type</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {(d.consultation_types||[]).includes("general") && (
                <button onClick={()=>setType("general")} className={`text-left p-4 rounded-lg border ${type==="general" ? "border-[#4a90d9] bg-[#ebf3fc]" : "border-[#e2e8f0]"}`} data-testid="type-general">
                  <div className="font-semibold text-[#1a2b4a]">General</div>
                  <div className="text-xs text-[#475569]">15 min · <Money amount={pricing?.general_price ?? 50}/></div>
                </button>
              )}
              {(d.consultation_types||[]).includes("specialist") && (
                <button onClick={()=>setType("specialist")} className={`text-left p-4 rounded-lg border ${type==="specialist" ? "border-[#4a90d9] bg-[#ebf3fc]" : "border-[#e2e8f0]"}`} data-testid="type-specialist">
                  <div className="font-semibold text-[#1a2b4a]">Specialist</div>
                  <div className="text-xs text-[#475569]">20 min · <Money amount={pricing?.specialist_price ?? 70}/></div>
                </button>
              )}
            </div>
          </div>

          <div className="cm-card">
            <div className="flex items-center gap-2 mb-3"><Calendar size={16} className="text-[#4a90d9]"/><h3 className="font-semibold text-[#1a2b4a]">Pick a date</h3></div>
            <div className="grid grid-cols-7 gap-2">
              {dates.map((d,i) => (
                <button key={i} onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                  className={`p-2 rounded-lg border text-center ${selectedDate?.toDateString()===d.toDateString() ? "border-[#4a90d9] bg-[#ebf3fc]" : "border-[#e2e8f0] hover:border-[#4a90d9]"}`}
                  data-testid={`date-${i}`}>
                  <div className="text-xs text-[#475569] uppercase">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
                  <div className="text-sm font-semibold text-[#1a2b4a]">{d.getDate()}</div>
                </button>
              ))}
            </div>
            {selectedDate && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2"><Clock size={14} className="text-[#4a90d9]"/><div className="text-sm font-semibold text-[#1a2b4a]">Available slots</div></div>
                {slots.length === 0 ? <div className="text-sm text-[#475569]">No slots this day — try another.</div> : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {slots.map(t => (
                      <button key={t} onClick={()=>setSelectedSlot(t)}
                        className={`text-sm rounded-lg py-2 border ${selectedSlot===t ? "border-[#4a90d9] bg-[#4a90d9] text-white" : "border-[#e2e8f0] text-[#1a2b4a] hover:border-[#4a90d9]"}`}
                        data-testid={`slot-${t}`}>{t}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="cm-card">
            <h3 className="font-semibold text-[#1a2b4a] mb-3">Help your doctor prepare</h3>
            <p className="text-xs text-[#475569] mb-3">This becomes part of the AI-assisted brief your doctor sees before your consult.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="cm-label">Reason for visit</label><input className="cm-input" value={reason} onChange={e=>setReason(e.target.value)} data-testid="book-reason"/></div>
              <div><label className="cm-label">Duration</label><input className="cm-input" placeholder="e.g. 3 days" value={duration} onChange={e=>setDuration(e.target.value)} data-testid="book-duration"/></div>
              <div className="sm:col-span-2"><label className="cm-label">Symptoms</label><textarea className="cm-input min-h-[70px]" value={symptoms} onChange={e=>setSymptoms(e.target.value)} data-testid="book-symptoms"/></div>
              <div><label className="cm-label">Severity</label>
                <select className="cm-input" value={severity} onChange={e=>setSeverity(e.target.value)} data-testid="book-severity">
                  <option value="">Choose</option><option>Mild</option><option>Moderate</option><option>Severe</option>
                </select>
              </div>
              <div className="sm:col-span-2"><label className="cm-label">Anything you want the doctor to know?</label><textarea className="cm-input min-h-[70px]" value={concerns} onChange={e=>setConcerns(e.target.value)} data-testid="book-concerns"/></div>
            </div>
          </div>
        </div>

        <div>
          <div className="cm-card lg:sticky lg:top-6">
            <h3 className="font-semibold text-[#1a2b4a] mb-3">Summary</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-[#475569]">Doctor</span><span className="font-medium text-[#1a2b4a]">Dr. {d.last_name}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Type</span><span className="font-medium capitalize text-[#1a2b4a]">{type}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Date</span><span className="font-medium text-[#1a2b4a]">{selectedDate?.toLocaleDateString() || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Time</span><span className="font-medium text-[#1a2b4a]">{selectedSlot || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Consultation</span><span className="font-medium text-[#1a2b4a]"><Money amount={price} currency={pricing?.currency}/></span></div>
              {balance > 0 && (
                <label className="flex items-start gap-2 rounded-lg border border-[#3daa6e]/30 bg-[#eaf7f0] p-2.5 cursor-pointer" data-testid="apply-credit-toggle">
                  <input type="checkbox" checked={applyCredit} onChange={e=>setApplyCredit(e.target.checked)} className="mt-0.5"/>
                  <div>
                    <div className="text-xs font-semibold text-[#065f46]">Apply account credit</div>
                    <div className="text-xs text-[#065f46]/80">You have <Money amount={balance} currency={pricing?.currency}/> available.</div>
                  </div>
                </label>
              )}
              {creditToApply > 0 && (
                <div className="flex justify-between text-[#3daa6e]"><span>Credit applied</span><span className="font-semibold">− <Money amount={creditToApply} currency={pricing?.currency}/></span></div>
              )}
              <div className="border-t border-[#e2e8f0] my-2 pt-2 flex justify-between text-base"><span className="text-[#1a2b4a] font-semibold">Due at checkout</span><span className="font-bold text-[#1a2b4a]" data-testid="due-now"><Money amount={dueNow} currency={pricing?.currency}/></span></div>
            </div>
            <button className="cm-btn-primary w-full mt-4" onClick={submit} disabled={loading} data-testid="book-pay-btn">
              {loading ? "Processing…" : dueNow <= 0 ? <>Confirm booking (fully covered by credit)</> : <>Continue to secure payment <ArrowRight size={14}/></>}
            </button>
            <p className="text-xs text-[#94a3b8] text-center mt-3">{dueNow > 0 ? "Powered by Stripe · Test card: 4242 4242 4242 4242" : "No card needed — credit covers it."}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
